import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Brain, Check, ChevronRight, CircleAlert, Coins, Image as ImageIcon,
    Pencil, ThumbsDown, ThumbsUp, X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CsAgentQuestion } from '../../../../common/models/content-studio.model';
import { awaitingOwnCapture, selfRunningEdit } from './plan-compiler';
import { describeFailure } from '../canvas/failure-copy';
import { resolveCreditCost } from '../recipe-pricing';
import { surfaceShotName } from '../recipe-schema';
import { CompiledPlan } from './plan-compiler';

/** One exchange in the conversation. */
export interface Turn {
    id: string;
    /** Server run id, once created — needed to rate the turn. */
    runId?: string;
    message: string;
    /** Readable activity lines derived from tool calls. */
    activity: string[];
    /** The model's own reasoning, behind the "Thought briefly" disclosure. */
    thoughts: string[];
    status: 'running' | 'done' | 'error';
    plan?: CompiledPlan;
    summary?: string;
    question?: CsAgentQuestion;
    error?: string;
    applied?: boolean;
    feedback?: 'up' | 'down';
    /** Whether this turn asked the model to reason. Gates the disclosure. */
    thinkingEnabled?: boolean;
    /**
     * Reference pictures attached to THIS message. Shown inside the sent
     * bubble — an attachment belongs to the message that carried it, not
     * floating over the composer after send as though it were still pending.
     */
    references?: Array<{ id: string; url: string; name?: string }>;
    /** Nodes this message was pointed at, so it can be read back later. */
    targets?: Array<{ nodeId: string; kind: string; label: string; thumbnailUrl?: string }>;
    /**
     * The canvas started this turn, not the vendor — the agent asked to be
     * called back once a plan's images had finished. Rendered as a note rather
     * than a vendor message, because showing it in their own voice would put
     * words in their mouth.
     */
    auto?: boolean;
    /**
     * They answered by clicking rather than typing.
     *
     * Rendered beside the agent's work rather than as a message in their voice:
     * picking "the Halden Beige, the two-seater" off a card is choosing from
     * what was offered, and echoing it back as though they had composed the
     * sentence made the conversation twice as long and half as clear.
     */
    choice?: { label: string; thumbnailUrl?: string };
    /** The vendor turned this plan down. Kept, so the record is honest. */
    denied?: boolean;
    /**
     * The generations that already existed when this turn was applied.
     *
     * Anything on the canvas that is not in this list arrived because of the
     * turn, which is how its pictures find their way back into the
     * conversation — the vendor asked here and should see the result here,
     * not only by hunting for a node on the canvas.
     */
    producedAfter?: string[];
}

/**
 * A pulsing status line. The label changes with what the agent is doing, but
 * the animation is constant — a still "Thinking…" reads as a hang.
 */
/**
 * What the vendor picked, shown as a chip rather than a message.
 *
 * Clicking "the two-seater" off a card is choosing from what was offered. Echoed
 * back as a chat bubble it read as though they had typed the sentence, which
 * made the conversation twice as long and half as clear — and, with a product,
 * lost the picture they had actually recognised it by.
 */
export const ChoiceChip: React.FC<{
    choice: { label: string; thumbnailUrl?: string };
}> = ({ choice }) => (
    <span
        className={cn(
            'mb-2 inline-flex max-w-full items-center gap-1.5 rounded-md border bg-muted py-0.5 pe-2.5',
            choice.thumbnailUrl ? 'ps-1' : 'ps-2.5',
        )}
    >
        {choice.thumbnailUrl && (
            <img
                src={choice.thumbnailUrl}
                alt=""
                className="size-5 shrink-0 rounded-full bg-muted object-cover"
            />
        )}
        <span className="min-w-0 truncate text-[11px] font-medium text-foreground">
            {choice.label}
        </span>
    </span>
);

export const AgentStatus: React.FC<{ label: string }> = ({ label }) => (
    <div className="mb-2 flex animate-pulse items-center gap-2 text-xs text-muted-foreground motion-reduce:animate-none motion-reduce:opacity-80">
        <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground" />
        <span>{label}</span>
    </div>
);

/**
 * What the agent did, in the order it did it.
 *
 * A turn used to show only its latest line, which vanished the moment the next
 * one replaced it — so a finished turn said nothing about how it got there. The
 * trail stays, and reads as a short record: the verb, then the thing it acted
 * on.
 */
export const ActionTrail: React.FC<{
    actions: Array<{ verb: string; subject?: string }>;
    /** The last one is still happening. */
    live?: boolean;
}> = ({ actions, live }) => {
    if (actions.length === 0) return null;

    return (
        <div className="mb-2 flex flex-col gap-1">
            {actions.map((action, i) => {
                const pending = live && i === actions.length - 1;
                return (
                    <div
                        // Actions are a positional record, not a keyed set.
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${action.verb}-${i}`}
                        className={cn(
                            'flex items-center gap-1.5 text-xs text-muted-foreground',
                            pending && 'animate-pulse motion-reduce:animate-none',
                        )}
                    >
                        <Pencil className="size-3.5 shrink-0 opacity-70" />
                        <span>{action.verb}</span>
                        {action.subject && (
                            <span className="max-w-[180px] truncate rounded-md border bg-muted px-1.5 py-px text-[11px] font-medium text-foreground">
                                {action.subject}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * The model's reasoning, collapsed by default.
 *
 * Shown because a vendor who can see why the agent did something can correct
 * it; hidden by default because most of the time they only want the answer.
 */
export const ThoughtDisclosure: React.FC<{ thoughts: string[] }> = ({ thoughts }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    if (thoughts.length === 0) return null;

    return (
        <div className="mb-2">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                // Keeps the label, icon and chevron on one line however long
                // the translation runs.
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
                {/* Brain — the reasoning, not the clock */}
                <Brain className="size-4 shrink-0" />
                {t('CONTENT_STUDIO.PLANNER.THOUGHT_BRIEFLY', 'Thought briefly')}
                <ChevronRight
                    className={cn(
                        'size-3.5 shrink-0 transition-transform duration-200',
                        open && 'rotate-90',
                    )}
                />
            </button>

            {/* Grid-rows trick: animates to the content's natural height without
                measuring it, and collapses smoothly rather than snapping. */}
            <div
                className={cn(
                    'grid transition-all duration-200 motion-reduce:transition-none',
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
            >
                <div className="overflow-hidden">
                    <div className="mt-1.5 border-s-2 ps-3 text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground">
                        {thoughts.join('\n\n')}
                    </div>
                </div>
            </div>
        </div>
    );
};

/** A proposed plan, rendered as an approval card inside the conversation. */
/**
 * What a plan actually does, as a short list of verbs and subjects.
 *
 * Read from the compiled plan rather than from the tool calls: the vendor cares
 * that a design was updated, not that `propose_plan` was called. Kept in step
 * with the plan card by being derived from the same object.
 */
export function planActions(
    compiled: CompiledPlan,
    t: (key: string, fallback?: any, opts?: any) => string,
): Array<{ verb: string; subject?: string }> {
    const actions: Array<{ verb: string; subject?: string }> = [];

    if (compiled.clearsCanvas) {
        actions.push({ verb: t('CONTENT_STUDIO.PLANNER.ACTION_CLEAR', 'Clear the canvas') });
    }
    for (const step of compiled.steps) {
        actions.push({
            verb: t('CONTENT_STUDIO.PLANNER.ACTION_ADD', 'Add'),
            subject: step.recipe.name,
        });
    }
    for (const mod of compiled.modifications) {
        actions.push({
            verb: t('CONTENT_STUDIO.PLANNER.ACTION_UPDATE', 'Update'),
            subject: mod.recipe?.name,
        });
    }
    for (const update of compiled.editUpdates) {
        actions.push({
            verb: t('CONTENT_STUDIO.PLANNER.ACTION_UPDATE', 'Update'),
            subject: update.design
                ? t('CONTENT_STUDIO.PLANNER.SUBJECT_DESIGN', 'Design')
                : t('CONTENT_STUDIO.PLANNER.SUBJECT_IMAGE', 'Image'),
        });
    }
    for (const action of compiled.assetActions) {
        if (action.kind === 'edit') {
            actions.push({
                verb: t('CONTENT_STUDIO.PLANNER.ACTION_EDIT', 'Edit'),
                subject: action.tool,
            });
        } else if (action.kind === 'retry') {
            actions.push({ verb: t('CONTENT_STUDIO.PLANNER.ACTION_RETRY', 'Retry'), subject: action.label });
        }
        // Accepting and rejecting are the vendor's decisions being recorded,
        // not work — they would pad the trail without telling them anything.
    }
    for (const gen of compiled.generations) {
        actions.push({
            verb: t('CONTENT_STUDIO.PLANNER.ACTION_GENERATE', 'Generate'),
            subject: gen.label,
        });
    }
    for (const story of compiled.videoStories || []) {
        actions.push({
            verb: t('VIDEO_EDITOR.STORY.ACTION'),
            subject: `${story.durationSec} s`,
        });
    }
    if (compiled.deletions.length > 0) {
        actions.push({
            verb: t('CONTENT_STUDIO.PLANNER.ACTION_REMOVE', 'Remove'),
            subject: String(compiled.deletions.flatMap((d) => d.removedIds).length),
        });
    }
    return actions;
}

/**
 * Thumbs on a finished turn.
 *
 * The only signal we get about whether the agent was actually useful — a plan
 * that compiles cleanly can still be the wrong plan, and nothing else in the
 * system would ever know.
 */
/**
 * The pictures a turn produced, shown in the conversation.
 *
 * The vendor asked here; the answer arriving only as a node somewhere on the
 * canvas means scrolling around to find out whether anything happened. A
 * thumbnail closes that loop, and clicking one opens it the same way the
 * canvas does.
 */
export const TurnResults: React.FC<{
    images: Array<{
        nodeId: string; url?: string; thumbnailUrl?: string;
        status?: string; label: string; error?: string;
    }>;
    onOpen: (nodeId: string) => void;
    /** Run a failed one again. Absent while another turn is working. */
    onRetry?: (nodeId: string) => void;
    /** Ones being run again right now, so the tile says so. */
    retrying?: Set<string>;
}> = ({ images, onOpen, onRetry, retrying }) => {
    const { t } = useTranslation();
    if (images.length === 0) return null;

    const failures = images.filter((i) => i.status === 'failed' && !retrying?.has(i.nodeId));
    const rest = images.filter((i) => i.status !== 'failed' || retrying?.has(i.nodeId));

    return (
        <div className="mt-2.5">
        {/* A failure is a sentence and a way forward, not a red square. Shown
            as its own row because "Failed" in a 72px tile told the vendor
            nothing about what went wrong or whether trying again would help —
            and left them to find the canvas node to do anything about it. */}
        {failures.map((image) => {
            const failure = describeFailure(image.error);
            return (
                <div
                    key={`fail-${image.nodeId}`}
                    className="mb-1.5 flex items-start gap-2 rounded-lg border bg-muted px-2.5 py-2"
                >
                    <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
                    <div className="min-w-0 flex-1">
                        <div className="text-xs leading-normal text-foreground">{failure.text}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{image.label}</div>
                    </div>
                    {failure.retryable && onRetry && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={() => onRetry(image.nodeId)}
                        >
                            {t('CONTENT_STUDIO.PLANNER.TRY_AGAIN', 'Try again')}
                        </Button>
                    )}
                </div>
            );
        })}

        <div className="flex flex-wrap gap-2">
            {rest.map((image) => {
                const ready = image.status === 'ready' && (image.thumbnailUrl || image.url);
                const isRetrying = retrying?.has(image.nodeId);
                const tile = (
                    <div className="flex size-[72px] items-center justify-center overflow-hidden rounded-lg border bg-muted transition-colors">
                        {ready ? (
                            <img
                                src={image.thumbnailUrl || image.url}
                                alt={image.label}
                                className="size-full object-cover"
                            />
                        ) : (
                            <span className="px-1 text-center text-[11px] text-muted-foreground">
                                {isRetrying
                                    ? t('CONTENT_STUDIO.PLANNER.RETRYING', 'Trying again…')
                                    : t('CONTENT_STUDIO.PLANNER.RENDERING', 'Rendering…')}
                            </span>
                        )}
                    </div>
                );
                return (
                    <Tooltip key={image.nodeId}>
                        <TooltipTrigger asChild>
                            {ready ? (
                                <button
                                    type="button"
                                    onClick={() => onOpen(image.nodeId)}
                                    className="p-0 [&:hover>div]:border-ring"
                                >
                                    {tile}
                                </button>
                            ) : (
                                <div>{tile}</div>
                            )}
                        </TooltipTrigger>
                        <TooltipContent>{image.label}</TooltipContent>
                    </Tooltip>
                );
            })}
        </div>
        </div>
    );
};

export const FeedbackControls: React.FC<{
    value?: 'up' | 'down';
    onRate: (value: 'up' | 'down') => void;
}> = ({ value, onRate }) => {
    const { t } = useTranslation();

    // Both thumbs are the same button with a different glyph; the only thing
    // that varies is which rating it records.
    const button = (kind: 'up' | 'down', title: string, Glyph: typeof ThumbsUp) => (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onRate(kind)}
                    aria-pressed={value === kind}
                    aria-label={title}
                    className={value === kind ? 'text-foreground' : 'text-muted-foreground'}
                >
                    {/* Filled once chosen, so the rating that was given reads at
                        a glance rather than only on hover. */}
                    <Glyph className={cn('size-4', value === kind && 'fill-current')} />
                </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{title}</TooltipContent>
        </Tooltip>
    );

    return (
        <div className="mt-1 flex gap-0.5">
            {button('up', t('CONTENT_STUDIO.PLANNER.RATE_UP', 'Good response') as string, ThumbsUp)}
            {button('down', t('CONTENT_STUDIO.PLANNER.RATE_DOWN', 'Bad response') as string, ThumbsDown)}
        </div>
    );
};

/**
 * What a plan costs in credits.
 *
 * Shared by the card and the pending bar so a vendor cannot be quoted one
 * number in one place and a different one in the other.
 */
export function planCost(compiled: CompiledPlan): number {
    const fromSteps = compiled.steps.reduce((total, s) => {
        const per = resolveCreditCost(s.recipe, {
            inputs: s.node.inputs,
            visualizationType: s.product?.visualizationType,
        });
        return total + per * (s.step.count || 1);
    }, 0);
    // Running an existing node costs the same as running a new one.
    const fromGenerations = compiled.generations
        .filter((g) => g.missing.length === 0)
        .reduce((total, g) => total + g.creditCost, 0);
    return fromSteps + fromGenerations;
}

/**
 * How much of a plan can run, what it changes, and what it makes.
 *
 * One place, because these three questions decide which buttons appear and had
 * drifted apart every time a new kind of operation was added.
 */
export function planCounts(compiled: CompiledPlan): {
    runnable: number;
    changesCanvas: boolean;
    kind: string;
    total: number;
    detail?: string;
} {
    // A node whose only gap is the frame this plan will capture counts as
    // runnable: the studio poses itself and the job follows.
    const readySteps = compiled.steps.filter((s) => s.missing.length === 0
        || awaitingOwnCapture(s.node.inputs as Record<string, any>, s.missing));
    // A plan that only runs existing nodes has nothing to "apply" — the plain
    // Apply button did literally nothing, which reads as a broken button.
    const changesCanvas = compiled.steps.length > 0
        || compiled.modifications.length > 0
        || compiled.deletions.length > 0
        || compiled.clearsCanvas
        // Accepting, rejecting and adding an edit node all change the canvas
        // without running anything, so they belong to Apply rather than Run.
        || compiled.assetActions.some((a) => a.kind !== 'retry')
        || compiled.productSwaps.length > 0
        || compiled.editUpdates.length > 0
        || !!compiled.videoStories?.length;
    // An edit can leave a node needing something it did not need before —
    // switching the 3D studio on requires a captured frame — and such a node
    // cannot run, so it must not be counted or promised.
    const runnableEdits = compiled.modifications.filter((m) => m.coercion.missing.length === 0
        || awaitingOwnCapture({ ...m.previous, ...m.patch }, m.coercion.missing));
    // An edit the plan configured completely runs itself once applied.
    const selfRunningEdits = compiled.assetActions.filter(
        (a) => a.kind === 'edit' && selfRunningEdit(a),
    );
    const runnable = readySteps.length
        + runnableEdits.length
        + compiled.generations.filter((g) => g.missing.length === 0).length
        + compiled.assetActions.filter((a) => a.kind === 'retry').length
        + selfRunningEdits.length
        + compiled.editUpdates.length
        // A video story runs itself once applied: storyboard, shots, clips.
        + (compiled.videoStories?.length || 0);

    // What the plan makes, named the way the vendor would name it.
    const video = compiled.steps.some((s) => s.recipe.outputKind === 'video')
        || !!compiled.videoStories?.length;
    const onlyDesign = compiled.steps.length === 0
        && (compiled.editUpdates.some((u) => u.design)
            || compiled.assetActions.some((a) => a.kind === 'edit' && a.tool === 'design'));
    const kind = video ? 'Video' : onlyDesign ? 'Design' : 'Image';

    const total = compiled.steps.reduce((n, s) => n + (s.step.count || 1), 0)
        + compiled.generations.length
        + compiled.assetActions.filter((a) => a.kind === 'edit' || a.kind === 'retry').length
        + compiled.editUpdates.length
        + compiled.modifications.length
        + (compiled.videoStories?.length || 0);

    // The line under the summary: what it runs on, briefly.
    const names = [
        ...compiled.steps.map((s) => s.recipe.name),
        ...compiled.assetActions
            .filter((a) => a.kind === 'edit')
            .map((a) => a.tool || '')
            .filter(Boolean),
    ];
    const aspect = compiled.steps
        .map((s) => (s.node.inputs as Record<string, any>)?.aspectRatio)
        .find(Boolean);
    const detail = [[...new Set(names)].slice(0, 2).join(' · '), aspect]
        .filter(Boolean).join(' · ') || undefined;

    return { runnable, changesCanvas, kind, total: Math.max(total, 1), detail };
}

/**
 * The decision, held where the decision is made.
 *
 * A proposal used to sit inline in the conversation, so scrolling back through
 * the history scrolled the buttons away with it — and the vendor lost the one
 * thing the panel was waiting on them for. This sits above the composer and
 * stays there until it is answered: what will be made, what it costs, and the
 * two ways out.
 */
export const PendingPlanBar: React.FC<{
    turn: Turn;
    creditBalance?: number | null;
    running: boolean;
    onApply: (turn: Turn, generate: boolean) => void;
    onDeny: (turn: Turn) => void;
}> = ({ turn, creditBalance, running, onApply, onDeny }) => {
    const { t } = useTranslation();
    const compiled = turn.plan!;

    const cost = useMemo(() => planCost(compiled), [compiled]);
    const counts = useMemo(() => planCounts(compiled), [compiled]);
    const canAfford = creditBalance == null || creditBalance >= cost;
    const hasDestructive = compiled.clearsCanvas || compiled.deletions.length > 0;

    return (
        // No shadow: it sits inside the panel rather than over it, and its
        // border is enough to hold it apart from the conversation.
        <div className="mb-2 overflow-hidden rounded-xl border bg-card">
            {/* What kind of thing, and how many of it. */}
            <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1.5">
                <ImageIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{counts.kind}</span>
                {counts.total > 1 && (
                    <span className="rounded-sm bg-muted px-1.5 py-px text-[11px] font-medium text-muted-foreground">
                        {`×${counts.total}`}
                    </span>
                )}
            </div>

            {/* What it will actually make, in the agent's words. */}
            {turn.summary && (
                <div className="mx-3 mb-2.5 max-h-[132px] overflow-y-auto rounded-md bg-muted px-2.5 py-2 text-sm leading-normal text-foreground">
                    {turn.summary}
                </div>
            )}

            {counts.detail && (
                <div className="px-3 pb-2 text-[11px] text-muted-foreground">{counts.detail}</div>
            )}

            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                <div
                    className={cn(
                        'flex items-center gap-1.5 text-[11px]',
                        canAfford ? 'text-muted-foreground' : 'text-destructive',
                    )}
                >
                    <Coins className="size-3.5 shrink-0" />
                    {cost > 0
                        ? t('CONTENT_STUDIO.PLANNER.COST_SHORT', {
                            defaultValue: '≈ {{cost}} credits', cost: Math.ceil(cost),
                        })
                        : t('CONTENT_STUDIO.PLANNER.NO_COST', 'Costs nothing')}
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onDeny(turn)}>
                        {t('CONTENT_STUDIO.PLANNER.DENY', 'Deny')}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={running || (counts.runnable > 0 && !canAfford)}
                        onClick={() => onApply(turn, counts.runnable > 0)}
                        // A plan that removes work is outlined red, never filled:
                        // the warning belongs on the edge, not in the fill.
                        className={cn(hasDestructive && DESTRUCTIVE_BUTTON)}
                    >
                        {counts.runnable > 0
                            ? t('CONTENT_STUDIO.PLANNER.RUN', 'Run')
                            : t('CONTENT_STUDIO.PLANNER.APPLY', 'Apply')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const PlanCard: React.FC<{
    turn: Turn;
    creditBalance?: number | null;
    running: boolean;
    onApply: (turn: Turn, generate: boolean) => void;
}> = ({ turn, creditBalance, running, onApply }) => {
    const { t } = useTranslation();
    const compiled = turn.plan!;

    const estimatedCost = useMemo(() => planCost(compiled), [compiled]);
    const { runnable: runnableCount } = useMemo(() => planCounts(compiled), [compiled]);

    return (
        <div className="overflow-hidden rounded-xl border bg-card">
            {turn.summary && (
                <div className="px-3.5 py-2.5 text-sm font-medium text-foreground">{turn.summary}</div>
            )}

            {compiled.clearsCanvas && (
                <PlanRow destructive>
                    <div className="text-sm text-destructive">
                        {t('CONTENT_STUDIO.PLANNER.CLEARS_CANVAS', {
                            defaultValue: 'Removes everything on the canvas ({{count}} nodes)',
                            count: compiled.clearedCount,
                        })}
                    </div>
                </PlanRow>
            )}

            {compiled.deletions.map((d) => (
                <PlanRow key={d.targetNodeId} destructive>
                    <div className="text-sm text-destructive">
                        {t('CONTENT_STUDIO.PLANNER.REMOVES', 'Removes')} {d.label}
                    </div>
                    <div className={PLAN_LABEL}>
                        {d.generationsLost > 0
                            ? t('CONTENT_STUDIO.PLANNER.REMOVES_WITH_IMAGES', {
                                defaultValue: '{{nodes}} nodes, including {{images}} generated images',
                                nodes: d.removedIds.length, images: d.generationsLost,
                            })
                            : t('CONTENT_STUDIO.PLANNER.REMOVES_NODES', {
                                defaultValue: '{{count}} nodes', count: d.removedIds.length,
                            })}
                    </div>
                </PlanRow>
            ))}

            {compiled.productSwaps.map((sw) => (
                <PlanRow key={`swap-${sw.productNodeId}`}>
                    <div className={PLAN_BODY}>
                        {t('CONTENT_STUDIO.PLANNER.REPLACES_PRODUCT', 'Replaces')}
                        {sw.from ? ` ${sw.from} → ` : ' '}
                        {sw.product.name}
                    </div>
                    {sw.affectedRecipes > 1 && (
                        <div className={PLAN_LABEL}>
                            {t('CONTENT_STUDIO.PLANNER.SWAP_AFFECTS', {
                                defaultValue: 'Affects {{count}} nodes using this product',
                                count: sw.affectedRecipes,
                            })}
                        </div>
                    )}
                </PlanRow>
            ))}

            {compiled.assetActions.map((a) => (
                <PlanRow
                    key={`asset-${a.kind}-${a.generationNodeId}`}
                    destructive={a.kind === 'reject'}
                >
                    <div className={cn(PLAN_BODY, a.kind === 'reject' && 'text-destructive')}>
                        {a.kind === 'retry' && t('CONTENT_STUDIO.PLANNER.RETRIES', 'Retries')}
                        {a.kind === 'approve' && t('CONTENT_STUDIO.PLANNER.ACCEPTS', 'Accepts')}
                        {a.kind === 'reject' && t('CONTENT_STUDIO.PLANNER.REJECTS', 'Rejects')}
                        {a.kind === 'edit' && t('CONTENT_STUDIO.PLANNER.EDITS', 'Edits')}
                        {' '}
                        {a.label}
                        {a.kind === 'edit' && a.tool && (
                            <span className="text-muted-foreground">{` · ${a.tool}`}</span>
                        )}
                    </div>
                    {a.step.rationale && <div className={PLAN_LABEL}>{a.step.rationale}</div>}
                </PlanRow>
            ))}

            {compiled.editUpdates.map((u) => (
                <PlanRow key={`edit-update-${u.editNodeId}`}>
                    <div className={PLAN_BODY}>
                        {t('CONTENT_STUDIO.PLANNER.UPDATES_EDIT', 'Changes')}
                        {' '}
                        {u.design
                            ? t('CONTENT_STUDIO.PLANNER.UPDATES_DESIGN', 'the design')
                            : t('CONTENT_STUDIO.PLANNER.UPDATES_SHAPE', 'the shape')}
                        <span className="text-muted-foreground">
                            {u.design?.headline ? ` · “${u.design.headline}”` : ''}
                            {u.aspectRatio ? ` · ${u.aspectRatio}` : ''}
                        </span>
                    </div>
                    {u.step.rationale && <div className={PLAN_LABEL}>{u.step.rationale}</div>}
                </PlanRow>
            ))}

            {compiled.generations.map((g) => (
                <PlanRow key={`gen-${g.targetNodeId}`}>
                    <div className={PLAN_BODY}>
                        {t('CONTENT_STUDIO.PLANNER.GENERATES', 'Generates')} {g.label}
                    </div>
                    {g.missing.length > 0 && (
                        <div className={cn(PLAN_LABEL, 'text-warning')}>
                            {t('CONTENT_STUDIO.PLANNER.GENERATE_BLOCKED', {
                                defaultValue: 'Cannot run yet — {{fields}} still needed',
                                fields: g.missing.join(', '),
                            })}
                        </div>
                    )}
                </PlanRow>
            ))}

            {compiled.modifications.map((m) => (
                <PlanRow key={m.targetNodeId}>
                    <div className={PLAN_BODY}>
                        {t('CONTENT_STUDIO.PLANNER.UPDATES', 'Updates')} {surfaceShotName(m.recipe.slug, { ...m.previous, ...m.patch }, m.product?.visualizationType) || m.recipe.name}
                        {m.product?.name && (
                            <span className="text-muted-foreground">
                                {' · '}{m.product.name}
                            </span>
                        )}
                    </div>
                    {Object.keys(m.patch).length > 0 && (
                        <div className={PLAN_LABEL}>
                            {Object.entries(m.patch).map(([k, v]) => (
                                <span key={k} className="me-2.5">
                                    {k}: <span className="line-through opacity-60">
                                        {String(m.previous[k] ?? '—')}
                                    </span>{' → '}{String(v)}
                                </span>
                            ))}
                        </div>
                    )}
                    {m.coercion.missing.length > 0 && (
                        <div className={cn(PLAN_LABEL, 'text-warning')}>
                            {t('CONTENT_STUDIO.PLANNER.EDIT_BLOCKS_RUN', {
                                defaultValue: 'Will need {{fields}} before it can run',
                                fields: m.coercion.missing.join(', '),
                            })}
                        </div>
                    )}
                </PlanRow>
            ))}

            {(compiled.videoStories || []).map((story, i) => (
                <PlanRow key={`story-${story.step.id || i}`}>
                    <div className={cn(PLAN_BODY, 'font-medium')}>
                        {t('VIDEO_EDITOR.STORY.PLAN_ROW', { seconds: story.durationSec })}
                        <span className="font-normal text-muted-foreground">
                            {' · '}{story.aspect}
                            {story.productNames.length > 0 && ` · ${story.productNames.join(', ')}`}
                        </span>
                    </div>
                    {story.brief && <div className={PLAN_LABEL}>{story.brief}</div>}
                </PlanRow>
            ))}

            {compiled.steps.map((s, i) => (
                <PlanRow key={s.node.id} className="flex gap-2.5">
                    <div className={cn(PLAN_LABEL, 'min-w-4 pt-px tabular-nums')}>{i + 1}</div>
                    <div className="min-w-0 flex-1">
                        <div className={cn(PLAN_BODY, 'font-medium')}>
                            {s.recipe.name}
                            {s.product?.name && (
                                <span className="font-normal text-muted-foreground">
                                    {' · '}{s.product.name}
                                </span>
                            )}
                        </div>
                        {s.step.rationale && <div className={PLAN_LABEL}>{s.step.rationale}</div>}
                        {s.missing.length > 0 && (
                            <div className={cn(PLAN_LABEL, 'text-warning')}>
                                {t('CONTENT_STUDIO.PLANNER.NEEDS_SETUP', 'Needs a detail before it can run')}
                            </div>
                        )}
                    </div>
                </PlanRow>
            ))}

            {compiled.dropped.length > 0 && (
                <PlanRow className={PLAN_LABEL}>
                    {t('CONTENT_STUDIO.PLANNER.DROPPED', {
                        defaultValue: "{{count}} step couldn't be built and was left out",
                        count: compiled.dropped.length,
                    })}
                </PlanRow>
            )}

            {/* What became of it. The decision itself is made in the bar
                above the composer, so this is a record rather than a prompt —
                a green "Applied to canvas" under a full plan read as though
                something still needed doing. */}
            <PlanRow className="flex items-center gap-1.5">
                {turn.denied
                    ? <X className="size-3.5 shrink-0 text-muted-foreground" />
                    : <Check className="size-3.5 shrink-0 text-success" />}
                <div className={PLAN_LABEL}>
                    {turn.denied
                        ? t('CONTENT_STUDIO.PLANNER.DENIED', 'Turned down')
                        : runnableCount > 0
                            ? t('CONTENT_STUDIO.PLANNER.APPLIED_RUNNING', {
                                defaultValue: 'On the canvas · {{count}} running',
                                count: runnableCount,
                            })
                            : t('CONTENT_STUDIO.PLANNER.APPLIED', 'On the canvas')}
                </div>
                {!turn.denied && estimatedCost > 0 && (
                    <div className={cn(PLAN_LABEL, 'ms-auto')}>
                        {t('CONTENT_STUDIO.PLANNER.COST_SHORT', {
                            defaultValue: '≈ {{cost}} credits', cost: Math.ceil(estimatedCost),
                        })}
                    </div>
                )}
            </PlanRow>
        </div>
    );
};

/**
 * One line of a plan.
 *
 * Every kind of operation the card lists — a swap, a deletion, a step — is the
 * same bordered row, so it is written once rather than eight times.
 */
const PlanRow: React.FC<{
    children: React.ReactNode;
    /** Removals are marked on the edge, not filled in red. */
    destructive?: boolean;
    className?: string;
}> = ({ children, destructive, className }) => (
    <div
        className={cn(
            'border-t px-3.5 py-2',
            destructive && 'border-s-2 border-s-destructive bg-destructive/5',
            className,
        )}
    >
        {children}
    </div>
);

const PLAN_LABEL = 'text-xs text-muted-foreground';
const PLAN_BODY = 'text-sm leading-normal text-foreground';

/** Destructive means outlined red — never a filled red button. */
const DESTRUCTIVE_BUTTON =
    'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive';
